import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateInvoiceInput, CreatePaymentInput, UpdateInvoiceInput } from '@/lib/marina/validators'

export type InvoiceStatus = 'PRO_FORMA' | 'DRAFT' | 'SENT' | 'PARTIALLY_PAID' | 'PAID' | 'OVERDUE' | 'CANCELLED'

export interface Payment {
  id: string
  invoiceId: string
  amountIdr: string
  method: 'CASH' | 'BANK_TRANSFER' | 'CARD' | 'CHECK' | 'OTHER'
  paidAt: string
  reference: string | null
  notes: string | null
  createdAt: string
}

export interface InvoiceLineItem {
  id: string
  description: string
  quantity: string
  unitPriceIdr: string
  amountIdr: string
  sortOrder: number
}

export interface Invoice {
  id: string
  code: string
  customerId: string
  customer?: {
    id: string
    firstName: string
    lastName: string
    email: string | null
    category: string
  }
  boatId: string | null
  boat?: { id: string; name: string } | null
  bookingId: string | null
  booking?: { id: string; code: string } | null
  quoteId: string | null
  quote?: { id: string; code: string } | null
  issueDate: string
  dueDate: string
  recipientName: string | null
  recipientEmail: string | null
  recipientAddress: string | null
  recipientTaxId: string | null
  recipientCompanyName: string | null
  subtotalIdr: string
  taxRate: string
  taxIdr: string
  totalIdr: string
  paidIdr: string
  currency: string
  fxRateToBase: string | null
  status: InvoiceStatus
  notes: string | null
  lineItems?: InvoiceLineItem[]
  payments?: Payment[]
  createdAt: string
  updatedAt: string
}

export interface InvoicesListResponse {
  items: Invoice[]
  total: number
  page: number
  pageSize: number
}

export interface InvoicesListParams {
  q?: string
  status?: InvoiceStatus
  customerId?: string
  overdue?: boolean
  page?: number
  pageSize?: number
  sort?: string
  order?: 'asc' | 'desc'
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function jsonFetch<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

function toQuery(params: Record<string, unknown>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '' && v !== false) qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export function useInvoices(params: InvoicesListParams = {}) {
  return useQuery({
    queryKey: ['invoices', params],
    queryFn: () => apiFetch<InvoicesListResponse>(`/api/invoices${toQuery(params as Record<string, unknown>)}`),
    staleTime: 15_000,
  })
}

export function useInvoiceStatusCounts() {
  return useQuery({
    queryKey: ['invoices', 'status-counts'],
    queryFn: () => apiFetch<{ counts: Record<string, number> }>('/api/invoices/status-counts'),
    staleTime: 15_000,
  })
}

export function useInvoice(id: string | null | undefined) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: () => apiFetch<{ invoice: Invoice }>(`/api/invoices/${id}`),
    enabled: !!id,
  })
}

export function useCreateInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateInvoiceInput) =>
      jsonFetch<{ invoice: Invoice }>('/api/invoices', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  })
}

export function useUpdateInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInvoiceInput }) =>
      jsonFetch<{ invoice: Invoice }>(`/api/invoices/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice', v.id] })
    },
  })
}

function itransInvoice(action: 'finalize' | 'send' | 'cancel') {
  return (id: string) =>
    jsonFetch<{ invoice: Invoice }>(`/api/invoices/${id}/${action}`, { method: 'POST', body: '{}' })
}

export function useFinalizeInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: itransInvoice('finalize'),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice', id] })
    },
  })
}
export function useSendInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: itransInvoice('send'),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice', id] })
    },
  })
}
export function useCancelInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: itransInvoice('cancel'),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice', id] })
    },
  })
}

export function useCreatePayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, data }: { invoiceId: string; data: CreatePaymentInput }) =>
      jsonFetch<{ payment: Payment; newStatus: string; newPaid: number }>(`/api/invoices/${invoiceId}/payments`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice', v.invoiceId] })
    },
  })
}

export function useDeletePayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, payId }: { invoiceId: string; payId: string }) =>
      jsonFetch<{ ok: boolean }>(`/api/invoices/${invoiceId}/payments/${payId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice', v.invoiceId] })
    },
  })
}
